import { BasketApiLineItem, BasketApiOffering } from './types';
import { LineItemProps, OfferingsProps, ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { parseServiceSuggestions } from '../templates/components/pdp/utils/product-service';
import { toCamelCase } from './common/objects';
import { ServiceIds } from './constants/services';
import { getWarrantyPeriodString } from './cart/services/getWarrantyPeriodString';
import { CartResponseType } from '@jbhi-fi/apis/src/cart/types';
import { JbCart } from '@jbhi-fi/apis/src/shopify/types/JbCart';

export const LEARN_MORE_KEY = 'learn-more';
const SERVICE_OFFERINGS_STORAGE_KEY = 'serviceOfferings';

export const getRequiredServiceOffering = <T extends BasketApiOffering | OfferingsProps>(
  productSku: string,
  serviceId: ServiceIds,
  offerings: Array<T>
): Array<T> => {
  const isPascalCase = offerings?.length > 0 && 'Id' in offerings[0];

  const idKey = isPascalCase ? 'Id' : 'id';
  const nameKey = isPascalCase ? 'Name' : 'name';

  const isShowNZAppleCareTheftEnabled = window.featureFlags?.isShowNZAppleCareTheftEnabled;

  if ([ServiceIds.MobileExtras, ServiceIds.Install].includes(serviceId) && offerings.length > 0) {
    return offerings.filter(o => o[idKey as keyof T]);
  }

  if (serviceId === ServiceIds.AppleCareNz && offerings.length > 1) {
    return offerings.filter(o => {
      const name = o[nameKey as keyof T] as string;
      return isShowNZAppleCareTheftEnabled
        ? name.toLowerCase().includes('theft')
        : !name.toLowerCase().includes('theft');
    });
  }

  return offerings.length > 1 ? [offerings[0]] : offerings;
};

export const parseServiceOfferings = (lineItem: BasketApiLineItem): Array<ServiceOffering> | null => {
  return lineItem.Services?.flatMap(service => {
    const serviceTypeId = service.Id;
    return service.Offerings.map((offering): ServiceOffering => {
      return {
        id: `${lineItem.Id}-${offering.Id}`,
        typeId: serviceTypeId,
        primaryItemPrice: lineItem.LineTotalInc,
        sku: offering.Sku ?? '',
        productSku: lineItem.SKU,
        offeringName: offering.Name,
        generalTitle: offering?.GeneralTitle,
        disclaimerText: offering.DisclosureText,
        priceInc: offering.PriceInc,
        description: offering.Description,
        vendorName: service.Vendor?.Name || '',
        learnMoreUrl: service.RelatedLinks.find(link => link.Id === LEARN_MORE_KEY)?.Url,
        priceType: offering.PriceType,
        extraCareCoveragePeriod: getWarrantyPeriodString(toCamelCase(offering?.SuggestedWarrantyPeriods)),
        shouldRender: service.shouldRender || false,
        requiredServiceData: service.RequiredData.filter(data => data.Scope === 'System'),
        priceRange: toCamelCase(offering.PriceRange),
      };
    });
  });
};

export type CartServiceOfferings = Record<string, Array<ServiceOffering> | LineItemProps>;

const formatSuggestionLineItem = (lineItem: any) => {
  const { errorMessage, lineTotalInc, sKU, services } = lineItem;
  return {
    errorMessage,
    productSku: sKU,
    finalProductPriceInc: lineTotalInc,
    services: [...services],
  };
};

const getProductInformationOfProductsInCart = (
  cart: JbCart | undefined
): Array<{ productSku: string; productGroupId?: number }> => {
  if (!cart?.items) return [];
  return cart.items.map(item => ({
    productSku: item.sku,
    productGroupId: item.properties._productGroup ? Number(item.properties._productGroup) : undefined,
  }));
};

export const getCartServiceOfferings = (basketResponse: CartResponseType, cart?: JbCart): CartServiceOfferings => {
  if (!basketResponse?.LineItems) return {};

  const parsedLineItems = parseServiceSuggestions(
    basketResponse.LineItems,
    getProductInformationOfProductsInCart(cart)
  );
  return parsedLineItems.reduce((acc: any, basketLineItem: BasketApiLineItem) => {
    const lineId = basketLineItem.Id;
    const serviceItem = acc[`${lineId}`];
    if (basketLineItem.IsAttachedService || serviceItem?.length > 0) return { ...acc };
    return {
      ...acc,
      [`${lineId}`]: parseServiceOfferings(basketLineItem),
      [`${lineId}|fullOffering`]: formatSuggestionLineItem(toCamelCase(basketLineItem)),
    };
  }, {});
};

export const getServiceOfferings = (): CartServiceOfferings | undefined => {
  const serviceOfferingsString = window.sessionStorage.getItem(SERVICE_OFFERINGS_STORAGE_KEY);
  if (serviceOfferingsString) {
    return JSON.parse(serviceOfferingsString) as CartServiceOfferings;
  }
  return;
};

export const setServiceOfferings = (
  basketResponse?: CartResponseType,
  cart?: JbCart
): CartServiceOfferings | undefined => {
  if (!basketResponse) return;
  const serviceOfferings = getCartServiceOfferings(basketResponse, cart);
  window.sessionStorage.setItem(SERVICE_OFFERINGS_STORAGE_KEY, JSON.stringify(serviceOfferings));
  return serviceOfferings;
};
